import {
  contentAreaStyles,
  contentStyles,
  headerStyles,
  modalStyles,
} from '@/components/portal/PatientIdModal/styles';
import ErrorState from '@/components/portal/RightPanel/ErrorState';
import LoadingState from '@/components/portal/RightPanel/LoadingState';
import NoteDisplay from '@/components/portal/RightPanel/NoteDisplay';
import { useNote } from '@/context/NoteProvider';
import { alpha, brand, text } from '@/styles/shared';
import { Close } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback } from 'react';

interface MobileNoteModalProps {
  open: boolean;
  onClose: () => void;
}

const MobileNoteModal = ({ open, onClose }: MobileNoteModalProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const noteContext = useNote();

  const noteError = noteContext.error !== null;
  const hasNote = !!noteContext.currentNote;
  const shouldShowLoading = noteContext.isLoading && !hasNote;

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.currentTarget === e.target) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isMobileOrTablet) return null; // Safety: only render on mobile/tablet

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus disableEnforceFocus>
      <Box sx={modalStyles} onClick={handleBackdropClick}>
        <Box sx={contentStyles} onClick={e => e.stopPropagation()}>
          {/* Header */}
          <Box sx={headerStyles}>
            <Typography
              variant="h6"
              sx={{
                ...text.title24,
                fontSize: { xs: '16px', lg: '18px' },
                fontWeight: 600,
                color: '#000000',
              }}
            >
              Note preview
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                padding: { xs: '6px', lg: '8px' },
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: alpha.purple05,
                  borderColor: brand.purpleAccent,
                },
              }}
              aria-label="Close"
            >
              <Close
                sx={{
                  fontSize: { xs: '18px', lg: '20px' },
                  color: '#666666',
                }}
              />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              ...contentAreaStyles,
              justifyContent: 'flex-start',
              overflowY: 'auto',
            }}
          >
            {shouldShowLoading && <LoadingState />}
            {noteError && <ErrorState />}
            {hasNote && !shouldShowLoading && <NoteDisplay />}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MobileNoteModal;
